import React from "react";
import PotentialIncentiveEarningsView from "../../views/potentialIncentiveEarnings";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { triggerShowHeader } from "../../redux/actions/action";
import config from "../../utils/config";
import {
  commonActionDispatcher,
  triggerFetchAllEndPoints,
  triggerJCAreaFilter,
} from "../../redux/actions";
import CustomLandingBody from "../../components/customLandingBody";
import { triggerIsDashboardAccessible } from "../../redux/actions";
import Spinner from "../../components/presentationals/Spinner";
import { mapTokenToRoleId } from "../../utils/common";

const PARTNER_ROLES_WITH_RUN_BUTTON = [
  "nhq",
  "region head",
  "state head",
  "jc",
  "area manager",
  "jpm",
];

/**
 * Partner dropdown stores either a string label or an object from filter APIs.
 * Using only partnerId broke when the API returned distId (and .split on an object throws).
 * JSON.stringify drops keys with undefined values, so a missing id produced {} as the body.
 */
const resolvePartnerDistId = (partner, orgIdFallback) => {
  if (partner == null || partner === "") {
    return orgIdFallback != null && String(orgIdFallback).trim() !== ""
      ? String(orgIdFallback).trim()
      : undefined;
  }
  if (typeof partner === "string") {
    const fromLabel = partner.split("-").pop()?.trim();
    if (fromLabel) return fromLabel;
    return orgIdFallback != null && String(orgIdFallback).trim() !== ""
      ? String(orgIdFallback).trim()
      : undefined;
  }
  const id =
    partner.partnerId ??
    partner.distId ??
    partner.distributorId ??
    partner.distributor_id ??
    partner.id;
  if (id != null && String(id).trim() !== "") {
    return String(id).trim();
  }
  return orgIdFallback != null && String(orgIdFallback).trim() !== ""
    ? String(orgIdFallback).trim()
    : undefined;
};

const PotentialIncentiveEarnings = () => {
  const dispatch = useDispatch();

  const [searchFields, setSearchFields] = useState({});
  const [distList, setDistList] = useState({
    loading: false,
    data: [],
    error: null,
  });
  const [csvLoading, setCsvLoading] = useState({});
  const [data, setData] = useState({});
  const [timestamp, setTimestamp] = useState(null);
  const [incentiveInfoData, setIncentiveInfoData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [dynamicHeaders, setDynamicHeaders] = useState({});
  const [structuredData, setStructuredData] = useState({});
  const [showInner, setShowInner] = useState(false);
  const [innerInfoData, setInnerInfoData] = useState({
    loading: false,
    data: null,
    error: null,
  });
  const [expandedTiles, setExpandedTiles] = useState({});
  const [selectedTilesBySection, setSelectedTilesBySection] = useState({});
  const [isInnerModalOpen, setIsInnerModalOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(null);
  const dashboardName = "Scheme Performance";
  const [show, setShow] = useState(false);
  /** Distributor id last committed by RUN (or impersonate). Tile APIs must not follow live dropdown changes. */
  const [tilesQueryDistId, setTilesQueryDistId] = useState(null);
  const impersonateFetchTriggered = useRef(false);
  /** Filters at last RUN (or impersonate load); timestamp/info must not follow live dropdown edits. */
  const schemePerformanceCommittedSearchRef = useRef({});

  const {
    filterValue: {
      partnerName,
      orgId,
      selectedRegion,
      selectedState,
      selectedPartnerRoleType,
      agentId,
      site,
      name,
      selectedArea,
    },
    partnerRoleType,
    filteredArray,
    getAllAPI: { potentialIncentiveEarningsObj, loading },
    tilesData,
  } = useSelector((state) => state.homeReducer);
  const { dashboardAccessibility } = useSelector(
    (state) => state?.dashboardAccessibilityReducer,
  );

  const distId = useMemo(
    () => resolvePartnerDistId(searchFields?.partner, orgId),
    [searchFields?.partner, orgId],
  );

  const csvActive = ["booking", "mtd", "act", "billed"];

  // Shared logic for Run click and impersonate auto-load - ensures same API flow
  const runSchemePerformance = useCallback(
    (fetchParams) => {
      setShow(true);
      setShowInner(false);
      dispatch(
        triggerFetchAllEndPoints(fetchParams, (err) => {
          setShow(false);
          setTilesQueryDistId(null);
        }),
      );
    },
    [dispatch],
  );

  const onRunClick = (searchFields) => {
    schemePerformanceCommittedSearchRef.current = searchFields ?? {};
    setTilesQueryDistId(
      resolvePartnerDistId(searchFields?.partner, orgId) ?? null,
    );
    const obj = {
      role:
        searchFields?.partner?.partnerRoleType ??
        (["jpm"]?.includes(partnerName?.toLowerCase())
          ? partnerRoleType
          : mapTokenToRoleId(searchFields?.partner?.split("-")?.[0])),
      roleType: partnerName?.toUpperCase(),
      partnerRoleType: partnerName,
      forTiles: "NO",
      site: site,
    };
    runSchemePerformance(obj);
  };

  const toggleTileExpansion = (tileKey) => {
    setExpandedTiles((prev) => ({
      ...prev,
      [tileKey]: !prev[tileKey],
    }));
  };

  // Handle tile selection for multi-select dropdown
  const handleTileSelection = (sectionId, selectedTiles, section) => {
    const highestPriorityTile = getHighestPriorityTile(section);

    // Always include the highest priority tile
    let finalSelection = [];
    if (highestPriorityTile) {
      finalSelection.push(highestPriorityTile);
    }

    // Add other selected tiles (excluding the highest priority tile)
    const otherSelectedTiles = selectedTiles.filter(
      (tile) => tile.tileName !== highestPriorityTile?.tileName,
    );

    const uniqueOtherTiles = otherSelectedTiles.filter(
      (tile, index, self) =>
        index === self.findIndex((t) => t.tileName === tile.tileName),
    );

    finalSelection = [...finalSelection, ...uniqueOtherTiles];

    setSelectedTilesBySection((prev) => ({
      ...prev,
      [sectionId]: finalSelection,
    }));
  };

  // Get available tiles for a section
  const getAvailableTilesForSection = (section) => {
    return (
      section?.secTilesMasterTxDtos
        ?.filter((tile) => tile.isTileActive === "Y")
        ?.map((tile) => ({
          tileName: tile.tileName,
          tileCode: tile.tileCode,
          priority: tile.priority,
        })) || []
    );
  };

  // Get the highest priority tile for a section
  const getHighestPriorityTile = (section) => {
    const availableTiles = getAvailableTilesForSection(section);
    if (availableTiles.length === 0) return null;

    return availableTiles.reduce((highest, current) =>
      current.priority > highest.priority ? current : highest,
    );
  };

  // Get filtered tiles for a section based on selection
  const getFilteredTilesForSection = (section) => {
    const availableTiles = getAvailableTilesForSection(section);
    const selectedTiles = selectedTilesBySection[section.sectionId] || [];
    const highestPriorityTile = getHighestPriorityTile(section);

    if (availableTiles.length <= 2) {
      return availableTiles;
    }
    let filteredTiles = [];
    if (highestPriorityTile) {
      filteredTiles.push(highestPriorityTile);
    }

    // Add selected tiles that are not the highest priority tile
    const additionalTiles = availableTiles.filter(
      (tile) =>
        selectedTiles.some((selected) => selected.tileName === tile.tileName) &&
        tile.tileName !== highestPriorityTile?.tileName,
    );
    filteredTiles = [...filteredTiles, ...additionalTiles];
    return filteredTiles;
  };

  const downloadCsv = (data, url) => {
    // Get headers from the first item in the data array
    const headers = data && data.length > 0 ? Object.keys(data[0]) : [];

    // Convert the data array into CSV format
    let csvContent =
      "data:text/csv;charset=utf-8," +
      headers.join(",") +
      "\n" +
      data
        .map((item, index) => {
          return headers.map((header) => {
            const value = item[header];
            // Handle all values that might contain commas, quotes, or newlines
            if (value !== null && value !== undefined) {
              const stringValue = String(value);
              // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
              if (
                stringValue.includes(",") ||
                stringValue.includes('"') ||
                stringValue.includes("\n") ||
                stringValue.includes("\r")
              ) {
                return `"${stringValue.replace(/"/g, '""')}"`;
              }
              return stringValue;
            }
            return "";
          });
        })
        .join("\n");

    // Create a download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const csvDownloadHandler = (obj) => {
    const distIdVal = resolvePartnerDistId(searchFields?.partner, orgId);
    if (!distIdVal) {
      return;
    }
    const body = {
      distId: distIdVal,
      sectionName: obj?.sectionName,
      tileCode: obj?.tileCode,
      kpi: obj?.kpi,
    };

    // Create a unique key for this specific download
    const loadingKey = `${obj?.sectionName}_${obj?.tileCode}_${obj?.kpi}`;

    setCsvLoading((prev) => {
      return {
        ...prev,
        [loadingKey]: true,
      };
    });
    dispatch(
      commonActionDispatcher({
        type: "POST",
        url: `${config.apiBasePath.baseIp}incentive-earnings`,
        params: body,
        headers: {
          url: obj?.url,
        },
        callback: (res) => {
          setCsvLoading((prev) => {
            return {
              ...prev,
              [loadingKey]: false,
            };
          });
          downloadCsv(res.content, obj?.url);
        },
        errCallback: (err) => {
          setCsvLoading((prev) => {
            return {
              ...prev,
              [loadingKey]: false,
            };
          });
        },
      }),
    );
  };

  const getAllPartnersList = useCallback(() => {
    if (searchFields.state) {
      const distListDto = {
        headers: {
          "Content-Type": "application/json",
        },
        type: "POST",
        url: `${config.apiBasePath.baseIp}filterDistList`,
        params: {
          state: searchFields?.state,
        },
        callback: (res) => {
          setDistList({
            loading: false,
            data: res.content,
            error: null,
          });
          setSearchFields((prev) => {
            return { ...prev, partner: res.content[0] };
          });
        },
        errCallback: (err) => {
          setDistList({
            loading: false,
            data: [],
            error: err.data,
          });
        },
      };
      setDistList({
        loading: true,
        data: [],
        error: null,
      });
      dispatch(commonActionDispatcher(distListDto));
    }
  }, [dispatch, searchFields?.state]);

  const fetchIncentiveInfo = useCallback(
    (searchFieldsOrParams) => {
      const isExplicitParams =
        typeof searchFieldsOrParams === "object" &&
        searchFieldsOrParams !== null &&
        "distId" in searchFieldsOrParams;
      const distId = isExplicitParams
        ? searchFieldsOrParams.distId
        : typeof searchFieldsOrParams === "string"
          ? searchFieldsOrParams
          : resolvePartnerDistId(searchFieldsOrParams?.partner, orgId);

      const params = {
        distId,
        ...(isExplicitParams &&
          searchFieldsOrParams?.sectionName && {
            sectionName: searchFieldsOrParams.sectionName,
          }),
        ...(isExplicitParams &&
          searchFieldsOrParams?.tileCode && {
            tileCode: searchFieldsOrParams.tileCode,
          }),
      };

      const incentiveInfoDto = {
        headers: {
          "Content-Type": "application/json",
          url: "/fetch-incentive-info-details",
        },
        params,
        type: "POST",
        url: `${config.apiBasePath.baseIp}incentive-earnings`,
        callback: (res) => {
          setIncentiveInfoData({
            loading: false,
            data: res.content,
            error: null,
          });
        },
        errCallback: (err) => {
          setIncentiveInfoData({
            loading: false,
            data: null,
            error: err.data,
          });
        },
      };
      setIncentiveInfoData({
        loading: true,
        data: null,
        error: null,
      });
      dispatch(commonActionDispatcher(incentiveInfoDto));
    },
    [dispatch, orgId],
  );

  const fetchInnerInfo = (obj) => {
    setInnerInfoData((prev) => ({
      ...prev,
      loading: true,
    }));
    const innerInfoDto = {
      headers: {
        "Content-Type": "application/json",
        url: obj?.url,
      },
      params: {
        distId: resolvePartnerDistId(searchFields?.partner, orgId),
        sectionName: obj?.sectionName,
        tileCode: obj?.tileCode,
        kpi: obj?.kpi,
      },
      type: "POST",
      url: `${config.apiBasePath.baseIp}incentive-earnings`,
      callback: (res) => {
        setInnerInfoData({
          loading: false,
          data: res.content,
          error: null,
        });
      },
      errCallback: (err) => {
        setInnerInfoData({
          loading: false,
          data: null,
          error: err,
        });
      },
    };
    dispatch(commonActionDispatcher(innerInfoDto));
  };

  const getUpdatedTimestamp = useCallback(
    (searchFields) => {
      const timeStampDto = {
        headers: {
          "Content-Type": "application/json",
          url: "/updated-timestamp",
        },
        type: "POST",
        params: {
          module: "Potential Incentive Earnings",
          partnerRole:
            searchFields?.partner?.partnerRoleType ??
            potentialIncentiveEarningsObj?.partnerRoleType ??
            mapTokenToRoleId(searchFields?.partner?.split("-")?.[0]) ??
            "752",
          subModule: "",
        },
        url: `${config.apiBasePath.baseIp}incentive-earnings`,
        callback: (res) => {
          setTimestamp(res.content?.latestTimestamp);
        },
        errCallback: (err) => {
          setTimestamp(err);
        },
      };
      dispatch(commonActionDispatcher(timeStampDto));
    },
    [dispatch, potentialIncentiveEarningsObj?.partnerRoleType],
  );

  const handleInfoClick = (sectionName) => {
    if (!sectionName) return;

    const data = incentiveInfoData?.data || {};
    const resolveInfoKey = (name) => {
      if (!name) return name;
      if (data[name]) return name;
      const t = String(name).trim();
      return Object.keys(data).find((k) => String(k).trim() === t) ?? name;
    };

    const sections = sectionName.includes(" & ")
      ? sectionName.split(" & ")
      : [sectionName];

    const fetchedData = sections.reduce((acc, section) => {
      const key = resolveInfoKey(section.trim());
      const sectionData = data[key];
      if (sectionData) {
        acc[section] = sectionData;
      }
      return acc;
    }, {});

    setSelectedData(fetchedData);
    // setIsModalOpen(true);
  };

  useEffect(() => {
    //check if isTestingActive is true, then send api call for triggerIsDashboardAccessible
    let isTestingActive = tilesData?.data?.content?.filter(
      (obj) => obj.moduleName === dashboardName,
    )[0].isTestingActive;

    //if it is true, then check if this logged in user has access to this dashboard
    if (isTestingActive) {
      //to check if the response for this dashboard api call is already cached in redux
      let isExistingDashboard = dashboardAccessibility?.data?.filter(
        (currentDashboard) => currentDashboard.dashboard === dashboardName,
      );

      //if dashboard accessibility is already cached and accessEnabled is false, means directly show alert without sending api call again
      if (
        isExistingDashboard.length > 0 &&
        !isExistingDashboard[0]?.isDashboardAccessible?.accessEnabled
      ) {
        // load dashboardcard home component and show alert
        return setShowAlert(true);
      }
      // if dashboard accessibility is cached and accessEnabled is true, do not send api call, do not show alert BUT SHOW DASHBOARD NORMALLY
      else if (
        isExistingDashboard.length > 0 &&
        isExistingDashboard[0]?.isDashboardAccessible?.accessEnabled
      ) {
        setShowAlert(false);
      }

      //if dashboard accessibility is not cached already then send api call for this dashboard
      else {
        const data = {
          baseParameter: {
            name: dashboardName,
            userType: site,
          },
        };

        const obj = {
          headers: {
            "Content-Type": "application/json",
            url: "get-dashboard-user-role-mapping",
          },
          params: data,
          extraData: "get-dashboard-user-role-mapping",
        };

        return dispatch(
          triggerIsDashboardAccessible(obj, (res) => {
            //if the response shows access enabled as false, go to home page and show alert
            if (!res.content.accessEnabled) {
              setShowAlert(true);
            } else {
              setShowAlert(false);
            }
          }),
        );
      }
    }

    //if isTestingActive is false, it is open for all users
    else {
      setShowAlert(false);
    }
  }, [loading]);

  useEffect(() => {
    setSearchFields({
      region: selectedRegion ? selectedRegion : config?.region[0],
      state: selectedState
        ? selectedState
        : config?.states?.[
            config?.regionMaps?.[
              selectedRegion
                ? selectedRegion
                : config?.region?.[0]?.toUpperCase()
            ]
          ]?.[0],
    });
    // dispatch(resetEdOrdersSummary());
  }, []);

  const fetchJCArea = useCallback(() => {
    setDistList({
      loading: true,
      data: null,
      error: null,
    });
    let obj = {
      params: {
        areaJcFlag: "JC",
        id: searchFields?.jc ?? selectedArea,
        jpFlag: "JC",
      },
    };
    const callback = (res) => {
      setDistList({
        loading: false,
        data: res?.content,
        error: null,
      });
      setSearchFields((prev) => ({
        ...prev,
        partner: res?.content?.[0],
      }));
    };
    dispatch(triggerJCAreaFilter(obj, "JC", callback));
  }, [dispatch, searchFields?.jc]);

  useEffect(() => {
    if (searchFields?.jc && filteredArray?.["JC"]?.length !== 0) {
      fetchJCArea();
    }
  }, [fetchJCArea, searchFields?.jc]);

  useEffect(() => {
    if (["jc", "area"]?.includes(partnerName?.toLowerCase())) {
      setDistList({
        loading: false,
        data: filteredArray?.["RDS/ARD/MARD"] ?? [],
        error: null,
      });
    } else if (partnerName?.toLowerCase()?.includes("jpm")) {
      setDistList({
        loading: false,
        data: filteredArray?.["All/JP"]?.slice(1) ?? [],
        error: null,
      });
    }
  }, []);

  useEffect(() => {
    if (
      ["nhq", "region head", "state head"].includes(partnerName.toLowerCase())
    )
      getAllPartnersList();
    else {
      if (partnerName?.toLowerCase()?.includes("jpm")) {
        setSearchFields((prev) => ({
          ...prev,
          partner: filteredArray?.["All/JP"]?.[1],
        }));
      } else if (partnerName?.toLowerCase()?.includes("area")) {
        setSearchFields((prev) => ({
          ...prev,
          jc: filteredArray?.["JC"]?.[0],
        }));
      } else {
        setSearchFields((prev) => ({
          ...prev,
          partner: filteredArray?.["RDS/ARD/MARD"]?.[0],
        }));
      }
    }
  }, [getAllPartnersList]);

  useEffect(() => {
    if (partnerName?.toLowerCase()?.includes("area")) {
      setSearchFields((prev) => ({
        ...prev,
        partner: filteredArray?.["RDS/ARD/MARD"]?.[0],
      }));
    }
  }, []);

  // Initialize selectedTilesBySection with all tiles when tiles load - accordion shows all tiles (same for Run and direct/impersonate)
  useEffect(() => {
    if (
      show &&
      potentialIncentiveEarningsObj?.modSecMasterTxDtos &&
      potentialIncentiveEarningsObj?.isModuleActive === "Y"
    ) {
      const initialSelection = {};
      potentialIncentiveEarningsObj.modSecMasterTxDtos
        .filter((row) => row.isSectionActive === "Y")
        .forEach((section) => {
          const availableTiles =
            section.secTilesMasterTxDtos
              ?.filter((tile) => tile.isTileActive === "Y")
              ?.map((tile) => ({
                tileName: tile.tileName,
                tileCode: tile.tileCode,
                priority: tile.priority,
              })) || [];
          if (availableTiles.length > 0) {
            initialSelection[section.sectionId] = availableTiles;
          }
        });
      setSelectedTilesBySection((prev) =>
        Object.keys(prev).length === 0 ? initialSelection : prev,
      );
    }
  }, [show, potentialIncentiveEarningsObj]);

  // Impersonate auto-load: same flow as Run click - when orgId present and no Run button (ARD, RDS, etc)
  useEffect(() => {
    const hasRunButton = PARTNER_ROLES_WITH_RUN_BUTTON.includes(
      partnerName?.toLowerCase(),
    );
    if (!orgId || hasRunButton) return;

    if (potentialIncentiveEarningsObj) {
      setShow(true);
      schemePerformanceCommittedSearchRef.current = { ...searchFields };
      setTilesQueryDistId(
        resolvePartnerDistId(searchFields?.partner, orgId) ?? null,
      );
    } else {
      // Call triggerFetchAllEndPoints only once (same as Run click) - prevents duplicate API calls
      if (!impersonateFetchTriggered.current) {
        impersonateFetchTriggered.current = true;
        schemePerformanceCommittedSearchRef.current = { ...searchFields };
        setTilesQueryDistId(
          resolvePartnerDistId(searchFields?.partner, orgId) ?? null,
        );
        const obj = {
          role:
            partnerRoleType ??
            mapTokenToRoleId((partnerName ?? "ARD").toUpperCase()),
          roleType: partnerName?.toUpperCase() ?? "ARD",
          partnerRoleType: partnerName ?? "ARD",
          forTiles: "NO",
          site: site,
        };
        runSchemePerformance(obj);
      }
    }
  }, [
    orgId,
    partnerName,
    potentialIncentiveEarningsObj,
    partnerRoleType,
    site,
    runSchemePerformance,
    searchFields?.partner,
  ]);

  // After RUN (or impersonate): timestamp + incentive-info use committed dist/filters only — not live dropdown.
  useEffect(() => {
    const needsRunGate = PARTNER_ROLES_WITH_RUN_BUTTON.includes(
      partnerName?.toLowerCase(),
    );
    if (!show) return;
    if (needsRunGate && !tilesQueryDistId) return;

    const committed = schemePerformanceCommittedSearchRef.current;
    getUpdatedTimestamp(committed);

    if (tilesQueryDistId) {
      fetchIncentiveInfo(tilesQueryDistId);
    } else {
      fetchIncentiveInfo(committed || orgId);
    }
  }, [
    show,
    tilesQueryDistId,
    fetchIncentiveInfo,
    getUpdatedTimestamp,
    partnerName,
    orgId,
  ]);

  useEffect(() => {
    if (
      potentialIncentiveEarningsObj &&
      potentialIncentiveEarningsObj?.isModuleActive === "Y" &&
      show &&
      tilesQueryDistId
    ) {
      const body = { distId: tilesQueryDistId };
      potentialIncentiveEarningsObj?.modSecMasterTxDtos
        .filter((row) => row.isSectionActive === "Y")
        .forEach((section) => {
          section.secTilesMasterTxDtos
            .filter((row) => row.isTileActive === "Y")
            .forEach((tile) => {
              const dataKey = `${section.sectionName}_${tile.tileName}`;
              const endpointKey = tile.tileEndPointUrl;

              setData((prevData) => {
                return {
                  ...prevData,
                  [endpointKey]: {
                    loading: true,
                    data: null,
                    error: null,
                  },
                };
              });

              // Initialize structured data
              setStructuredData((prevData) => {
                return {
                  ...prevData,
                  [dataKey]: {
                    sectionName: section.sectionName,
                    tileName: tile.tileName,
                    tileEndPointUrl: tile.tileEndPointUrl,
                    tileCode: tile.tileCode,
                    isCsvActive: tile.isCsvActive,
                    csvEndPointUrl: tile.csvEndPointUrl,
                    priority: tile.priority,
                    loading: true,
                    data: null,
                    error: null,
                  },
                };
              });
              dispatch(
                commonActionDispatcher({
                  type: "POST",
                  url: `${config.apiBasePath.baseIp}incentive-earnings`,
                  params: {
                    ...body,
                    sectionName: section.sectionName,
                    tileCode: tile.tileCode,
                  },
                  headers: {
                    url: tile?.tileEndPointUrl,
                  },
                  callback: (res) => {
                    setData((prev) => {
                      return {
                        ...prev,
                        [endpointKey]: {
                          loading: false,
                          data: res?.content,
                          error: null,
                        },
                      };
                    });

                    // Update structured data
                    setStructuredData((prev) => {
                      return {
                        ...prev,
                        [dataKey]: {
                          ...prev[dataKey],
                          loading: false,
                          data: res?.content,
                          error: null,
                        },
                      };
                    });
                    let responseHeaders = null;
                    if (res?.headers) {
                      responseHeaders = res.headers;
                    } else if (res?.content?.headers) {
                      responseHeaders = res.content.headers;
                    } else if (res?.metadata?.headers) {
                      responseHeaders = res.metadata.headers;
                    }

                    if (responseHeaders) {
                      setDynamicHeaders((prev) => ({
                        ...prev,
                        [tile.tileEndPointUrl]: responseHeaders,
                      }));
                    }
                  },
                  errCallback: (err) => {
                    setData((prev) => {
                      return {
                        ...prev,
                        [endpointKey]: {
                          loading: false,
                          data: null,
                          error: err,
                        },
                      };
                    });

                    setStructuredData((prev) => {
                      return {
                        ...prev,
                        [dataKey]: {
                          ...prev[dataKey],
                          loading: false,
                          data: null,
                          error: err,
                        },
                      };
                    });
                  },
                }),
              );
            });
        });
    }
  }, [potentialIncentiveEarningsObj, show, tilesQueryDistId, dispatch]);

  useEffect(() => {
    dispatch(triggerShowHeader(false));
  }, [dispatch]);

  return showAlert === null ? (
    <Spinner />
  ) : showAlert ? (
    <CustomLandingBody showAlert={showAlert} dashboardName={dashboardName} />
  ) : (
    <PotentialIncentiveEarningsView
      {...{
        distList,
        searchFields,
        setSearchFields,
        timestamp,
        onRunClick,
        partnerName,
        potentialIncentiveEarningsObj,
        data,
        structuredData,
        csvDownloadHandler,
        csvLoading,
        loading,
        csvActive,
        dynamicHeaders,
        handleInfoClick,
        isModalOpen,
        selectedData,
        setIsModalOpen,
        orgId,
        agentId,
        name,
        fetchInnerInfo,
        innerInfoData,
        setShowInner,
        showInner,
        incentiveInfoData,
        expandedTiles,
        setExpandedTiles,
        selectedTilesBySection,
        setSelectedTilesBySection,
        isInnerModalOpen,
        setIsInnerModalOpen,
        handleTileSelection,
        getHighestPriorityTile,
        getFilteredTilesForSection,
        getAvailableTilesForSection,
        toggleTileExpansion,
        filteredArray,
        show,
        partnerRoleType,
      }}
    />
  );
};

export default PotentialIncentiveEarnings;
